import { Component, computed, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import { IconComponent, TagComponent } from '@totalenergiescode/tds-angular';
import { filter } from 'rxjs';

import { applicationRouteNames } from '../../application-route-names';
import { formatDateTime } from '../../helpers/app-date.helper';
import { getStatusAccent, getTypeAccent } from '../../helpers/courrier-display.helper';
import { CourrierSearchResult } from '../../models/courrier.model';
import { CourrierSearchService } from '../../services/courrier-search.service';
import { SearchBridgeService } from '../../services/search-bridge.service';

@Component({
  selector: 'app-search-dropdown',
  standalone: true,
  imports: [TranslateModule, TagComponent, IconComponent],
  templateUrl: './search-dropdown.component.html',
  styleUrl: './search-dropdown.component.scss',
})
export class SearchDropdownComponent implements OnInit {
  private readonly searchService = inject(CourrierSearchService);
  private readonly searchBridge = inject(SearchBridgeService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  results = this.searchService.searchResults;
  isLoading = this.searchService.isLoading;
  query = this.searchBridge.query;

  show = computed(() => this.query().length >= 2 && this.searchBridge.visible());

  ngOnInit(): void {
    this.searchService.search$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((q) => {
      this.searchService.search({ q, pageSize: 3 });
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.searchBridge.setVisible(false);
      });
  }

  onResultClick(id: number): void {
    this.router.navigate([`/${applicationRouteNames.courrier.index}`], { queryParams: { detail: id } });
  }

  onSeeMore(): void {
    const q = this.query();
    if (q) {
      this.router.navigate([`/${applicationRouteNames.search}`], { queryParams: { q } });
    }
  }

  getTypeAccent(type: string): 'grey' | 'purple' {
    return getTypeAccent(type);
  }

  getStatusAccent(status: string): 'blue' | 'green' | 'pink' | 'orange' | 'teal' | 'grey' {
    return getStatusAccent(status);
  }

  // Exposed for the template — not re-declared, just re-bound to the shared helper (FR-027, FR-028).
  protected readonly formatDateTime = formatDateTime;

  getQueryTokens(): string[] {
    return this.query()
      .trim()
      .split(/[\s,]+/)
      .filter((t) => t.length > 0);
  }

  getFooterTokensText(): string {
    return this.getQueryTokens()
      .map((t) => `"${t}"`)
      .join(', ');
  }

  trackByResult(_index: number, result: CourrierSearchResult): number {
    return result.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (
      !target.closest('app-search-dropdown') &&
      !target.closest('tds-search') &&
      !target.closest('input[type="search"]')
    ) {
      this.searchBridge.setVisible(false);
    }
  }
}
